import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { IconWand } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useCreate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";

import type { Ingredient } from "../../types";

export function IngredientCreate() {
  const navigate = useNavigate();
  const { mutateAsync, isLoading } = useCreate<Ingredient>();

  const [categories, setCategories] = useState<string[]>([]);
  const [rarities, setRarities] = useState<string[]>([]);
  const [enumsLoading, setEnumsLoading] = useState(true);

  const [canonicalName, setCanonicalName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameJp, setNameJp] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [rarity, setRarity] = useState<string | null>(null);
  const [descriptionEn, setDescriptionEn] = useState("");
  const [descriptionJp, setDescriptionJp] = useState("");
  const [display, setDisplay] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingFields, setGeneratingFields] = useState(false);

  useEffect(() => {
    api.get("/ingredients/enums")
      .then((res) => {
        setCategories(res.data.categories ?? []);
        setRarities(res.data.rarities ?? []);
      })
      .catch(() => {/* non-fatal — dropdowns will be empty */})
      .finally(() => setEnumsLoading(false));
  }, []);

  const handleGenerateFields = async () => {
    if (!canonicalName.trim()) {
      setError("Enter a canonical name first.");
      return;
    }
    setGeneratingFields(true);
    try {
      const { data } = await api.post<{ name_en: string; name_jp: string; description_en: string; description_jp: string }>(
        "/menu-items/generate-ingredient-fields",
        {
          canonical_name: canonicalName.trim(),
          category: category || undefined,
          rarity: rarity || undefined,
          name_en: nameEn || undefined,
          name_jp: nameJp || undefined,
          description_en: descriptionEn || undefined,
          description_jp: descriptionJp || undefined,
        }
      );
      if (data.name_en && !nameEn) setNameEn(data.name_en);
      if (data.name_jp && !nameJp) setNameJp(data.name_jp);
      if (data.description_en && !descriptionEn) setDescriptionEn(data.description_en);
      if (data.description_jp && !descriptionJp) setDescriptionJp(data.description_jp);
    } catch {
      notifications.show({ color: "red", message: "Failed to generate fields" });
    } finally {
      setGeneratingFields(false);
    }
  };

  const handleSubmit = async () => {
    if (!canonicalName.trim()) {
      setError("Canonical name is required.");
      return;
    }

    try {
      setError(null);
      const res = await mutateAsync({
        resource: "ingredients",
        values: {
          canonical_name: canonicalName.trim(),
          name_en: nameEn.trim() || null,
          name_jp: nameJp.trim() || null,
          category: category || null,
          rarity: rarity || null,
          description_en: descriptionEn.trim() || null,
          description_jp: descriptionJp.trim() || null,
          display,
        },
      });

      navigate(`/admin/ingredients/edit/${res.data.id}`);
    } catch (e: any) {
      setError(e?.message ?? "Failed to create ingredient");
    }
  };

  if (enumsLoading) {
    return <Group justify="center" py="xl"><Loader /></Group>;
  }

  return (
    <Stack gap="md">
      <Title order={2}>Add ingredient</Title>

      {error ? <Alert color="red">{error}</Alert> : null}

      <Group align="flex-end" wrap="nowrap">
        <TextInput
          label="Canonical name"
          description="Unique identifier used internally (e.g. maguro, shiitake)"
          placeholder="e.g. maguro"
          required
          style={{ flex: 1 }}
          value={canonicalName}
          onChange={(e) => setCanonicalName(e.currentTarget.value)}
        />
        <Button
          variant="light"
          leftSection={<IconWand size={15} />}
          loading={generatingFields}
          disabled={!canonicalName.trim()}
          onClick={handleGenerateFields}
        >
          Generate fields
        </Button>
      </Group>

      <Group grow>
        <TextInput
          label="English name"
          placeholder="e.g. Tuna"
          value={nameEn}
          onChange={(e) => setNameEn(e.currentTarget.value)}
        />
        <TextInput
          label="Japanese name"
          placeholder="e.g. マグロ"
          value={nameJp}
          onChange={(e) => setNameJp(e.currentTarget.value)}
        />
      </Group>

      <Group grow>
        <Select
          label="Category"
          placeholder="Select a category"
          data={categories}
          value={category}
          onChange={setCategory}
          clearable
          searchable
        />
        <Select
          label="Rarity"
          placeholder="Select rarity"
          data={rarities}
          value={rarity}
          onChange={setRarity}
          clearable
        />
      </Group>

      <Textarea
        label="English description"
        placeholder="Short description in English"
        minRows={3}
        value={descriptionEn}
        onChange={(e) => setDescriptionEn(e.currentTarget.value)}
      />

      <Textarea
        label="Japanese description"
        placeholder="日本語での説明"
        minRows={3}
        value={descriptionJp}
        onChange={(e) => setDescriptionJp(e.currentTarget.value)}
      />

      <Switch
        label="Display publicly"
        description="When on, this ingredient is visible to customers on menu items."
        checked={display}
        onChange={(e) => setDisplay(e.currentTarget.checked)}
      />

      <Group justify="flex-end">
        <Button variant="default" onClick={() => navigate("/admin/ingredients")}>
          Cancel
        </Button>
        <Button loading={isLoading} onClick={handleSubmit}>
          Create
        </Button>
      </Group>
    </Stack>
  );
}
